import { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Stack,
  Divider,
  Skeleton,
  Tabs,
  Tab,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  TablePagination
} from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';

import { useNotificationSettings, updateNotificationSetting } from '../../../stores/notificationSettingStore';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';

export default function NotificationSettings({ companyOptions = [], selectedGeoCompany = [], onChangeGeoCompany = () => {} }) {
  const { t } = useLanguage();
  const [subTab, setSubTab] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const paginatedCompanies = companyOptions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const { settings, isLoading } = useNotificationSettings();
  const notify = useNotifier();

  const handleToggleNotif = async (keyName, isChecked, labelName) => {
    try {
      await updateNotificationSetting(keyName, isChecked);
      notify({
        message: `${t('mapsTracking.notification', 'Notifikasi')} ${labelName} ${t('mapsTracking.updatedSuccessfully', 'berhasil diperbarui.')}`,
        severity: 'success'
      });
    } catch {
      notify({
        message: `${t('mapsTracking.failedToUpdateNotification', 'Gagal memperbarui notifikasi')} ${labelName}.`,
        severity: 'error'
      });
    }
  };

  const isAllCompanySelected = !selectedGeoCompany || selectedGeoCompany.length === 0;

  const handleToggleCompany = (companyValue) => {
    const stringValue = String(companyValue);

    if (isAllCompanySelected) {
      const allOthers = companyOptions.map((c) => String(c.value)).filter((v) => v !== stringValue);
      onChangeGeoCompany(allOthers);
      return;
    }

    const currentSelected = Array.isArray(selectedGeoCompany) ? selectedGeoCompany.map(String) : [];

    if (currentSelected.includes(stringValue)) {
      onChangeGeoCompany(currentSelected.filter((val) => val !== stringValue));
    } else {
      const nextSelected = [...currentSelected, stringValue];
      if (nextSelected.length === companyOptions.length) {
        onChangeGeoCompany([]);
      } else {
        onChangeGeoCompany(nextSelected);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width="60%" height={30} sx={{ mb: 2 }} />
        {[1, 2, 3].map((item) => (
          <Card key={item} sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={40} />
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  const SettingRow = ({ label, flagKey }) => (
    <FormControlLabel
      sx={{ width: '100%', margin: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}
      labelPlacement="start"
      control={
        <Switch
          checked={settings[flagKey] || false}
          onChange={(e) => handleToggleNotif(flagKey, e.target.checked, label)}
          color="primary"
          size="small"
        />
      }
      label={
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', fontSize: '0.8rem' }}>
          {label}
        </Typography>
      }
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f8fafc' }}>
      {/* --- SUB-TABS NAVIGATION --- */}
      <Tabs
        value={subTab}
        onChange={(e, v) => setSubTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none', color: '#64748b' },
          '& .Mui-selected': { color: '#485D9A !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#485D9A' }
        }}
      >
        <Tab
          icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 18, mb: '-4px' }} />}
          iconPosition="start"
          label={t('mapsTracking.notifications', 'Notifikasi')}
        />
        <Tab
          icon={<CorporateFareIcon sx={{ fontSize: 18, mb: '-4px' }} />}
          iconPosition="start"
          label={t('mapsTracking.companyFilter', 'Company Filter')}
        />
      </Tabs>

      {/* --- KONTEN SUB-TABS --- */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, scrollbarWidth: 'thin' }}>
        {/* --- TAB 0: NOTIFIKASI --- */}
        {subTab === 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, px: 0.5 }}>
              {t('mapsTracking.choosePopupAlerts', 'Pilih peringatan *pop-up* yang ingin Anda terima saat memantau peta.')}
            </Typography>
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SensorsOutlinedIcon fontSize="small" sx={{ color: '#64748b' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#475569' }}>
                    {t('mapsTracking.engineAndStatus', 'Mesin & Status')}
                  </Typography>
                </Box>
                <Divider />
                <CardContent sx={{ p: '8px 12px !important' }}>
                  <SettingRow label={t('mapsTracking.overspeedLimit', 'Overspeed (Batas Kecepatan)')} flagKey="overSpeed" />
                  <SettingRow label={t('mapsTracking.powerAccuDisconnected', 'Power Accu Terputus')} flagKey="powerAccuOff" />
                  {/* <SettingRow label="Tombol Darurat (Panic Button)" flagKey="panicButton" /> */}
                  {/* <SettingRow label="Nyalakan Suara (Play Sound)" flagKey="playSound" /> */}
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorOutlineOutlinedIcon fontSize="small" sx={{ color: '#64748b' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#475569' }}>
                    {t('mapsTracking.drivingBehavior', 'Perilaku Mengemudi')}
                  </Typography>
                </Box>
                <Divider />
                <CardContent sx={{ p: '8px 12px !important' }}>
                  <SettingRow label={t('mapsTracking.suddenBraking', 'Pengereman Mendadak')} flagKey="harshBraking" />
                  <SettingRow label={t('mapsTracking.suddenAcceleration', 'Akselerasi Mendadak')} flagKey="harshAcceleration" />
                  <SettingRow label={t('mapsTracking.sharpCornering', 'Menikung Tajam')} flagKey="harshCornering" />
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RouteOutlinedIcon fontSize="small" sx={{ color: '#64748b' }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#475569' }}>
                    {t('mapsTracking.enterExitArea', 'Keluar / Masuk Area')}
                  </Typography>
                </Box>
                <Divider />
                <CardContent sx={{ p: '8px 12px !important' }}>
                  <SettingRow label={t('mapsTracking.enterPitArea', 'Masuk Area PIT (Pool)')} flagKey="enterPool" />
                  <SettingRow label={t('mapsTracking.exitPitArea', 'Keluar Area PIT (Pool)')} flagKey="exitPool" />
                  <SettingRow label={t('mapsTracking.enterJettyArea', 'Masuk Area Jetty (Customer)')} flagKey="enterCustomerPoint" />
                  <SettingRow label={t('mapsTracking.exitJettyArea', 'Keluar Area Jetty (Customer)')} flagKey="exitCustomerPoint" />
                  <SettingRow label={t('mapsTracking.enterGeofenceArea', 'Masuk Area Geofence')} flagKey="enterGeofence" />
                  <SettingRow label={t('mapsTracking.exitGeofenceArea', 'Keluar Area Geofence')} flagKey="exitGeofence" />
                </CardContent>
              </Card>
            </Stack>
          </>
        )}

        {/* --- TAB 1: FILTER COMPANY --- */}
        {subTab === 1 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, px: 0.5 }}>
              {t(
                'mapsTracking.chooseCompanyFilter',
                'Pilih perusahaan mana saja yang area/poligonnya ingin ditampilkan di peta dan daftar Geolocation.'
              )}
            </Typography>

            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0', bgcolor: '#fff' }}>
              <List disablePadding>
                <ListItem button onClick={() => onChangeGeoCompany([])} sx={{ py: 0.5 }}>
                  <Checkbox checked={isAllCompanySelected} size="small" />
                  <ListItemText
                    primary={t('mapsTracking.allCompaniesReset', 'Semua Perusahaan / Reset')}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: isAllCompanySelected ? 'bold' : 'normal',
                      color: isAllCompanySelected ? '#485D9A' : 'inherit'
                    }}
                  />
                </ListItem>
                <Divider />
                {paginatedCompanies.map((comp) => {
                  const isChecked =
                    isAllCompanySelected ||
                    (Array.isArray(selectedGeoCompany) && selectedGeoCompany.map(String).includes(String(comp.value)));
                  return (
                    <ListItem key={comp.value} button onClick={() => handleToggleCompany(comp.value)} sx={{ py: 0.5 }}>
                      <Checkbox checked={isChecked} size="small" />
                      <ListItemText primary={comp.label} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                    </ListItem>
                  );
                })}
              </List>
            </Card>

            <TablePagination
              component="div"
              count={companyOptions.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100, 200, 500, 1000]}
              labelRowsPerPage=""
              sx={{
                borderTop: 'none',
                mt: 1,
                '.MuiTablePagination-toolbar': {
                  minHeight: '36px',
                  pl: 0,
                  pr: 0
                },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  margin: 0,
                  fontSize: '0.75rem'
                },
                '.MuiTablePagination-select': {
                  fontSize: '0.75rem',
                  paddingLeft: '4px'
                },
                '.MuiTablePagination-actions': {
                  marginLeft: '4px'
                }
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
